import type { AttestationMode, MobilePolicy, PinningMode, ShareRule } from "@/api/adminBff";

/**
 * Tenant default policy — verbatim from Mobile/docs/phase0-contract.md §5
 * (MOBILE_APP_MAM.md §3.1 with blockJailbrokenRooted:false + attestationMode:"warn" per D-M5).
 */
export const DEFAULT_MOBILE_POLICY: MobilePolicy = {
    requireAppPin: true,
    allowBiometric: true,
    pinLength: 6,
    pinRetryLimit: 5,
    reAuthAfterMinutes: 15,
    blockJailbrokenRooted: false,
    minOsVersion: { ios: "17.0", android: "29" },
    minAppVersion: { ios: "0.1.0", android: "0.1.0" },
    blockClipboardOut: "blocked",
    blockShareOut: "blockedToUnmanaged",
    blockSaveToLocal: true,
    blockScreenshot: true,
    blockBackup: true,
    allowOpenInUnmanaged: false,
    requireManagedBrowser: true,
    offlineGraceHours: 72,
    maxOfflineDays: 30,
    wipeOnPolicyFail: true,
    networkAllowlistCidrs: [],
    attestationMode: "warn",
    pinningMode: "off",
    allowedAccounts: []
};

export const SHARE_RULES: Array<{ value: ShareRule; label: string }> = [
    { value: "allowed", label: "Allowed" },
    { value: "blockedToUnmanaged", label: "Only into other Athavita apps" },
    { value: "blocked", label: "Blocked" }
];

export const ATTESTATION_MODES: Array<{ value: AttestationMode; label: string }> = [
    { value: "off", label: "Off — don't check" },
    { value: "warn", label: "Warn — check and report, but allow" },
    { value: "block", label: "Block — refuse devices that fail" }
];

export const PINNING_MODES: Array<{ value: PinningMode; label: string }> = [
    { value: "off", label: "Off" },
    { value: "report", label: "Report only" },
    { value: "enforce", label: "Enforce" }
];

/** Android API level → marketing version, for the min-OS hint. */
export const ANDROID_API_LABELS: Record<number, string> = {
    29: "Android 10",
    30: "Android 11",
    31: "Android 12",
    32: "Android 12L",
    33: "Android 13",
    34: "Android 14",
    35: "Android 15",
    36: "Android 16"
};

export type NumericKey = "pinLength" | "pinRetryLimit" | "reAuthAfterMinutes" | "offlineGraceHours" | "maxOfflineDays";

/** Identity's ranges (MobilePolicyValidator) — also ported by admin-bff (MobileEndpoints.cs). */
export const NUMERIC_LIMITS: Record<NumericKey, { min: number; max: number; unit: string }> = {
    pinLength: { min: 4, max: 12, unit: "digits" },
    pinRetryLimit: { min: 1, max: 20, unit: "attempts" },
    reAuthAfterMinutes: { min: 1, max: 1440, unit: "minutes" },
    offlineGraceHours: { min: 1, max: 2160, unit: "hours" },
    maxOfflineDays: { min: 1, max: 365, unit: "days" }
};

/** Editor state: numbers and lists are kept as the raw text the admin typed. */
export type PolicyDraft = Omit<MobilePolicy, NumericKey | "networkAllowlistCidrs" | "allowedAccounts"> &
    Record<NumericKey, string> & { networkAllowlistCidrs: string; allowedAccounts: string };

/** Fills fields Identity didn't send with the contract defaults. */
export function withDefaults(p: Partial<MobilePolicy> | null | undefined): MobilePolicy {
    const d = DEFAULT_MOBILE_POLICY;
    const src = p ?? {};
    const pick = <K extends keyof MobilePolicy>(k: K): MobilePolicy[K] => (src[k] ?? d[k]) as MobilePolicy[K];
    return {
        requireAppPin: pick("requireAppPin"),
        allowBiometric: pick("allowBiometric"),
        pinLength: pick("pinLength"),
        pinRetryLimit: pick("pinRetryLimit"),
        reAuthAfterMinutes: pick("reAuthAfterMinutes"),
        blockJailbrokenRooted: pick("blockJailbrokenRooted"),
        minOsVersion: {
            ios: String(src.minOsVersion?.ios ?? d.minOsVersion.ios),
            android: String(src.minOsVersion?.android ?? d.minOsVersion.android)
        },
        minAppVersion: {
            ios: String(src.minAppVersion?.ios ?? d.minAppVersion.ios),
            android: String(src.minAppVersion?.android ?? d.minAppVersion.android)
        },
        blockClipboardOut: pick("blockClipboardOut"),
        blockShareOut: pick("blockShareOut"),
        blockSaveToLocal: pick("blockSaveToLocal"),
        blockScreenshot: pick("blockScreenshot"),
        blockBackup: pick("blockBackup"),
        allowOpenInUnmanaged: pick("allowOpenInUnmanaged"),
        requireManagedBrowser: pick("requireManagedBrowser"),
        offlineGraceHours: pick("offlineGraceHours"),
        maxOfflineDays: pick("maxOfflineDays"),
        wipeOnPolicyFail: pick("wipeOnPolicyFail"),
        networkAllowlistCidrs: [...pick("networkAllowlistCidrs")],
        attestationMode: pick("attestationMode"),
        pinningMode: pick("pinningMode"),
        allowedAccounts: [...pick("allowedAccounts")]
    };
}

export function toDraft(p: MobilePolicy): PolicyDraft {
    return {
        ...p,
        minOsVersion: { ...p.minOsVersion },
        minAppVersion: { ...p.minAppVersion },
        pinLength: String(p.pinLength),
        pinRetryLimit: String(p.pinRetryLimit),
        reAuthAfterMinutes: String(p.reAuthAfterMinutes),
        offlineGraceHours: String(p.offlineGraceHours),
        maxOfflineDays: String(p.maxOfflineDays),
        networkAllowlistCidrs: p.networkAllowlistCidrs.join("\n"),
        allowedAccounts: p.allowedAccounts.join("\n")
    };
}

export function splitLines(text: string, lower = false): string[] {
    const out: string[] = [];
    for (const raw of text.split(/[\n,;]+/)) {
        const v = lower ? raw.trim().toLowerCase() : raw.trim();
        if (v && !out.includes(v)) out.push(v);
    }
    return out;
}

/** Only call on a draft that passed {@link validateDraft}. */
export function fromDraft(d: PolicyDraft): MobilePolicy {
    return {
        ...d,
        minOsVersion: { ios: d.minOsVersion.ios.trim(), android: d.minOsVersion.android.trim() },
        minAppVersion: { ios: d.minAppVersion.ios.trim(), android: d.minAppVersion.android.trim() },
        pinLength: Number(d.pinLength),
        pinRetryLimit: Number(d.pinRetryLimit),
        reAuthAfterMinutes: Number(d.reAuthAfterMinutes),
        offlineGraceHours: Number(d.offlineGraceHours),
        maxOfflineDays: Number(d.maxOfflineDays),
        networkAllowlistCidrs: splitLines(d.networkAllowlistCidrs),
        allowedAccounts: splitLines(d.allowedAccounts, true)
    };
}

// ── Validation ───────────────────────────────────────────────────────────────
// Same rules and limits as Identity's MobilePolicyValidator (EnterpriseIdentity/service/Mobile/MobilePolicy.cs),
// which admin-bff ports line for line (Api/MobileEndpoints.cs). Change all three together. Identity reports only
// its first failure; here every field is checked so the form can mark them all at once.

export const MAX_CIDRS = 256;
export const MAX_ACCOUNTS = 500;
const INT32_MAX = 2_147_483_647;

/** .NET `int.TryParse(s, NumberStyles.None)`: digits only, fits in an int. */
function parseInt32(s: string): number | null {
    return /^\d+$/.test(s) && Number(s) <= INT32_MAX ? Number(s) : null;
}

/** Identity's MobileVersion.TryParse: dotted non-negative integers ("17", "17.5", "0.1.0"), at most 32 chars. */
function parseVersion(s: string): number[] | null {
    if (!s.trim() || s.length > 32) return null;
    const parts = s.split(".").map(parseInt32);
    return parts.every((p): p is number => p !== null) ? parts : null;
}

function parseIPv4(s: string): number[] | null {
    const parts = s.split(".");
    if (parts.length !== 4 || parts.some((p) => !/^(0|[1-9]\d{0,2})$/.test(p) || Number(p) > 255)) return null;
    return parts.map(Number);
}

/** 16 bytes, or null. Handles `::` compression and a trailing dotted-quad (`::ffff:192.0.2.1`). */
function parseIPv6(input: string): number[] | null {
    let s = input;
    if (s.includes(".")) {
        const i = s.lastIndexOf(":");
        const v4 = i < 0 ? null : parseIPv4(s.slice(i + 1));
        if (!v4) return null;
        const [a = 0, b = 0, c = 0, d = 0] = v4;
        s = `${s.slice(0, i + 1)}${((a << 8) | b).toString(16)}:${((c << 8) | d).toString(16)}`;
    }
    if (!/^[0-9a-f:]+$/i.test(s)) return null;
    const halves = s.split("::");
    if (halves.length > 2) return null;
    const groups = (h: string | undefined) => (h ? h.split(":") : []);
    const left = groups(halves[0]);
    const right = halves.length === 2 ? groups(halves[1]) : [];
    if ([...left, ...right].some((g) => !/^[0-9a-f]{1,4}$/i.test(g))) return null;
    const missing = 8 - left.length - right.length;
    if (halves.length === 2 ? missing < 1 : missing !== 0) return null;
    return [...left, ...Array<string>(halves.length === 2 ? missing : 0).fill("0"), ...right]
        .map((g) => parseInt(g, 16))
        .flatMap((h) => [h >> 8, h & 0xff]);
}

/**
 * Identity accepts only what .NET `IPNetwork.TryParse` does: `address/prefix` with the prefix inside the address
 * family and no bits set after it (10.0.0.0/8, not 10.1.2.3/8). A bare address is not accepted. Null = valid.
 */
export function cidrProblem(value: string): string | null {
    const slash = value.lastIndexOf("/");
    const address = slash < 0 ? value : value.slice(0, slash);
    const bytes = parseIPv4(address) ?? parseIPv6(address);
    if (!bytes) return `"${value}" is not an IP address range.`;
    const bits = bytes.length * 8;
    if (slash < 0) return `"${value}" needs a prefix length — for a single address write ${value}/${bits}.`;
    const prefix = parseInt32(value.slice(slash + 1));
    if (prefix === null || prefix > bits) return `"${value}" needs a prefix length from 0 to ${bits}.`;
    for (let bit = prefix; bit < bits; bit++) {
        if ((((bytes[bit >> 3] ?? 0) >> (7 - (bit & 7))) & 1) === 1) {
            return `"${value}" isn't a network address — use the start of the range, e.g. 10.0.0.0/8 rather than 10.1.2.3/8.`;
        }
    }
    return null;
}

/** Identity's rule: exactly one "@" ("user@domain" or "@domain"), at most 320 characters, a dot after the "@". */
export function isAllowedAccount(value: string): boolean {
    const at = value.indexOf("@");
    return at >= 0 && at === value.lastIndexOf("@") && value.length <= 320 && value.slice(at + 1).includes(".");
}

/** Field-keyed error messages; empty object = valid. Keys: draft keys, or `minOsVersion.ios` etc. */
export function validateDraft(d: PolicyDraft): Record<string, string> {
    const errors: Record<string, string> = {};

    (Object.keys(NUMERIC_LIMITS) as NumericKey[]).forEach((k) => {
        const { min, max, unit } = NUMERIC_LIMITS[k];
        const n = parseInt32(d[k].trim());
        if (n === null || n < min || n > max) {
            errors[k] = `Enter a whole number from ${min} to ${max} ${unit}.`;
        }
    });

    const ios = parseVersion(d.minOsVersion.ios.trim());
    if (!ios) {
        errors["minOsVersion.ios"] = "Use a version like 17.0 or 17.4.1.";
    } else if ((ios[0] ?? 0) < 17) {
        errors["minOsVersion.ios"] = "The apps need iOS 17 or later — use 17.0 or higher.";
    }
    const api = parseInt32(d.minOsVersion.android.trim());
    if (api === null) {
        errors["minOsVersion.android"] = "Use an Android API level, e.g. 29 for Android 10.";
    } else if (api < 29) {
        errors["minOsVersion.android"] = "The apps need Android 10 (API 29) or later.";
    }
    (["ios", "android"] as const).forEach((p) => {
        if (!parseVersion(d.minAppVersion[p].trim())) {
            errors[`minAppVersion.${p}`] = "Use a dotted version number like 0.1.0.";
        }
    });

    const cidrs = splitLines(d.networkAllowlistCidrs);
    const badCidr = cidrs.map(cidrProblem).find((p) => p !== null);
    if (cidrs.length > MAX_CIDRS) errors.networkAllowlistCidrs = `At most ${MAX_CIDRS} ranges.`;
    else if (badCidr) errors.networkAllowlistCidrs = badCidr;

    const accounts = splitLines(d.allowedAccounts, true);
    const badAccount = accounts.find((a) => !isAllowedAccount(a));
    if (accounts.length > MAX_ACCOUNTS) errors.allowedAccounts = `At most ${MAX_ACCOUNTS} entries.`;
    else if (badAccount) errors.allowedAccounts = `"${badAccount}" is not an email address or @domain.`;

    return errors;
}

// ── Device list helpers ──────────────────────────────────────────────────────

export type DeviceStatusKey = "active" | "revoked" | "wipe-pending" | "wiped" | "unknown";

/** Tolerates `wipe_pending` / `wipePending` / `pending-wipe` spellings from Identity. */
export function normaliseDeviceStatus(raw: string | null | undefined): DeviceStatusKey {
    const k = (raw ?? "").toLowerCase().replace(/[^a-z]/g, "");
    switch (k) {
        case "active":
            return "active";
        case "revoked":
            return "revoked";
        case "wipepending":
        case "pendingwipe":
        case "wiperequested":
            return "wipe-pending";
        case "wiped":
            return "wiped";
        default:
            return "unknown";
    }
}

export type AttestationKey = "verified" | "unverified" | "failed" | "unknown";

export function normaliseAttestation(raw: string | null | undefined): AttestationKey {
    const k = (raw ?? "").toLowerCase();
    return k === "verified" || k === "unverified" || k === "failed" ? k : "unknown";
}

export const APP_LABELS: Record<string, string> = {
    mail: "Mail",
    connect: "Connect",
    authenticator: "Authenticator"
};

export function platformLabel(p: string | null | undefined): string {
    const k = (p ?? "").toLowerCase();
    if (k === "ios") return "iOS";
    if (k === "android") return "Android";
    return p || "—";
}

/**
 * OS column text. Android devices report `osVersion` as the API level ("34"), iOS as the marketing version
 * ("17.5"): → "Android API 34" (hint "Android 14") / "iOS 17.5".
 */
export function osLabel(platform: string | null | undefined, osVersion: string | null | undefined): { text: string; hint?: string } {
    const v = (osVersion ?? "").trim();
    const p = (platform ?? "").toLowerCase();
    if (!v) return { text: `${platformLabel(platform)} —` };
    if (p === "android") {
        if (/^\d+$/.test(v)) {
            const name = ANDROID_API_LABELS[Number(v)];
            return name ? { text: `Android API ${v}`, hint: name } : { text: `Android API ${v}` };
        }
        return { text: `Android ${v}` };
    }
    if (p === "ios") return { text: `iOS ${v}` };
    return { text: platform ? `${platformLabel(platform)} ${v}` : v };
}

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

export function relativeTime(iso: string | null | undefined): string {
    if (!iso) return "Never";
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return "—";
    const secs = Math.round((t - Date.now()) / 1000);
    const abs = Math.abs(secs);
    if (abs < 60) return rtf.format(secs, "second");
    if (abs < 3600) return rtf.format(Math.round(secs / 60), "minute");
    if (abs < 86_400) return rtf.format(Math.round(secs / 3600), "hour");
    if (abs < 86_400 * 30) return rtf.format(Math.round(secs / 86_400), "day");
    return new Date(t).toLocaleDateString();
}
